<script>
import { api } from "@/state/services";

const CAMPANHA = "atualizacao-metas-2026";

/**
 * Pesquisa NPS pública (/nps, sem login) sobre a atualização: planejamento de metas, sugestão
 * pela média, resumo no Dashboard e Rankings. Grava em POST nps/resposta (salesteams_app_api,
 * controllers/Nps.php). O campo `website` é um honeypot invisível contra robôs.
 */
export default {
  beforeCreate() {
    document.body.className = "auth";
  },
  data() {
    return {
      notas: Array.from({ length: 11 }, (_, i) => i),
      novidadesOpcoes: [
        { value: "planejamento", text: "Planejamento de metas" },
        { value: "sugestao_media", text: "Sugestão de meta pela minha média" },
        { value: "dashboard_metas", text: "Adesões do mês no Dashboard" },
        { value: "dashboard_numeros", text: "Meus números no Dashboard" },
        { value: "rankings", text: "Ranking Nacional / Estadual" },
      ],
      ajudouOpcoes: [
        { value: "sim", text: "Sim" },
        { value: "em_parte", text: "Em parte" },
        { value: "nao", text: "Não" },
      ],
      perfilOpcoes: [
        { value: "", text: "Prefiro não informar" },
        { value: "consultor", text: "Consultor(a)" },
        { value: "regional", text: "Regional" },
        { value: "secretaria", text: "Secretária" },
        { value: "outro", text: "Outro" },
      ],
      form: {
        nota: null,
        novidades: [],
        ajudou: "",
        gostou: "",
        sugestao: "",
        nome: "",
        perfil: "",
        website: "",
      },
      enviando: false,
      enviado: false,
      erro: "",
    };
  },
  methods: {
    classeNota(n) {
      const base = n <= 6 ? "is-detrator" : n <= 8 ? "is-neutro" : "is-promotor";
      return [base, { "is-sel": this.form.nota === n }];
    },
    enviar() {
      this.erro = "";
      if (this.form.nota === null) {
        this.erro = "Escolha uma nota de 0 a 10 na primeira pergunta.";
        return;
      }
      this.enviando = true;
      api
        .post("nps/resposta", { campanha: CAMPANHA, ...this.form })
        .then((r) => {
          if (r.data && r.data.status === "success") {
            this.enviado = true;
          } else {
            this.erro = (r.data && r.data.message) || "Não foi possível enviar agora. Tente novamente.";
          }
        })
        .catch(() => {
          this.erro = "Sem conexão com o servidor. Confira a sua internet e tente de novo.";
        })
        .finally(() => {
          this.enviando = false;
        });
    },
  },
};
</script>

<template>
  <div class="nps-page">
    <div class="nps-wrap">
      <div class="nps-header">
        <img src="@/assets/images/logo.png" alt="Sales Teams" />
      </div>

      <div class="nps-card">
        <!-- Obrigado -->
        <div v-if="enviado" class="text-center py-4" role="status">
          <div class="nps-ok" aria-hidden="true"><i class="bx bx-check-circle"></i></div>
          <h1 class="nps-titulo">Obrigado pela sua resposta!</h1>
          <p class="nps-texto mb-0">A sua opinião ajuda a deixar o Teams Connect cada vez melhor.</p>
        </div>

        <b-form v-else novalidate @submit.prevent="enviar">
          <h1 class="nps-titulo">Conta pra gente o que achou da atualização</h1>
          <p class="nps-texto">
            Lançamos o <strong>planejamento de metas</strong> com sugestão pela sua média, o resumo de
            <strong>metas e números no Dashboard</strong> e os <strong>Rankings Nacional e Estadual</strong>.
            Leva menos de 1 minuto.
          </p>

          <!-- 1. NPS -->
          <fieldset class="nps-q">
            <legend>1. De 0 a 10, quanto você recomendaria esta atualização a um colega? *</legend>
            <div class="nps-notas" role="radiogroup" aria-label="Nota de 0 a 10">
              <label v-for="n in notas" :key="n" class="nps-nota" :class="classeNota(n)">
                <input v-model="form.nota" type="radio" name="nota" :value="n" class="sr-only" />
                {{ n }}
              </label>
            </div>
            <div class="nps-escala" aria-hidden="true">
              <span>Nada provável</span><span>Muito provável</span>
            </div>
          </fieldset>

          <!-- 2. Novidades usadas -->
          <fieldset class="nps-q">
            <legend>2. Quais novidades você já usou?</legend>
            <b-form-checkbox-group v-model="form.novidades" :options="novidadesOpcoes" stacked />
          </fieldset>

          <!-- 3. Ajudou -->
          <fieldset class="nps-q">
            <legend>3. O planejamento de metas te ajudou a definir o seu objetivo do mês?</legend>
            <b-form-radio-group v-model="form.ajudou" :options="ajudouOpcoes" name="ajudou" />
          </fieldset>

          <!-- 4. Gostou -->
          <div class="nps-q">
            <label for="nps-gostou" class="nps-label">4. O que você mais gostou?</label>
            <b-form-textarea id="nps-gostou" v-model="form.gostou" rows="3" maxlength="2000" placeholder="Opcional" />
          </div>

          <!-- 5. Sugestão -->
          <div class="nps-q">
            <label for="nps-sugestao" class="nps-label">5. Tem alguma sugestão ou opinião? O que podemos melhorar?</label>
            <b-form-textarea id="nps-sugestao" v-model="form.sugestao" rows="4" maxlength="2000" placeholder="Opcional" />
          </div>

          <!-- Identificação opcional -->
          <div class="nps-q nps-ident">
            <div>
              <label for="nps-nome" class="nps-label">Seu nome</label>
              <b-form-input id="nps-nome" v-model="form.nome" maxlength="120" placeholder="Opcional" autocomplete="name" />
            </div>
            <div>
              <label for="nps-perfil" class="nps-label">Você é</label>
              <b-form-select id="nps-perfil" v-model="form.perfil" :options="perfilOpcoes" />
            </div>
          </div>

          <!-- Honeypot: invisível para pessoas -->
          <div class="nps-hp" aria-hidden="true">
            <label for="nps-website">Site</label>
            <input id="nps-website" v-model="form.website" type="text" tabindex="-1" autocomplete="off" />
          </div>

          <div v-if="erro" class="alert alert-danger py-2 small" role="alert">{{ erro }}</div>

          <b-button type="submit" block class="nps-enviar" :disabled="enviando">
            <b-spinner v-if="enviando" small class="mr-1" />
            {{ enviando ? "Enviando…" : "Enviar resposta" }}
          </b-button>
        </b-form>
      </div>

      <p class="nps-footer">&copy; {{ new Date().getFullYear() }} Sales Teams</p>
    </div>
  </div>
</template>

<style scoped>
.nps-page {
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(170deg, var(--st-brand-start) 0%, var(--st-brand) 50%, #1e3a5f 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 16px 64px;
  box-sizing: border-box;
}
.nps-wrap {
  width: 100%;
  max-width: 560px;
}
.nps-header {
  text-align: center;
  margin-bottom: 20px;
}
.nps-header img {
  max-width: 160px;
  width: 100%;
  filter: brightness(0) invert(1);
}
.nps-card {
  background: var(--st-surface);
  border-radius: var(--st-radius-lg);
  box-shadow: var(--st-shadow-lg);
  padding: 28px 24px;
}
.nps-titulo {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--st-brand);
  margin-bottom: 8px;
}
.nps-texto {
  font-size: 0.9rem;
  color: var(--st-text-muted);
  margin-bottom: 20px;
}
.nps-q {
  margin: 0 0 20px;
  padding: 0;
  border: 0;
}
.nps-q legend,
.nps-label {
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--st-text);
  margin-bottom: 10px;
}
.nps-notas {
  display: grid;
  grid-template-columns: repeat(11, minmax(0, 1fr));
  gap: 4px;
}
.nps-nota {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  margin: 0;
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius-sm);
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, color 0.15s;
}
.nps-nota:focus-within {
  outline: 2px solid var(--st-focus-ring);
  outline-offset: 2px;
}
.nps-nota.is-detrator.is-sel { background: var(--st-danger); border-color: var(--st-danger); color: var(--st-text-inverse); }
.nps-nota.is-neutro.is-sel { background: var(--st-warning); border-color: var(--st-warning); color: var(--st-text-inverse); }
.nps-nota.is-promotor.is-sel { background: var(--st-success); border-color: var(--st-success); color: var(--st-text-inverse); }
.nps-escala {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 0.75rem;
  color: var(--st-text-muted);
}
.nps-ident {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.nps-hp {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
.nps-enviar {
  background: #27ae60;
  border-color: #27ae60;
  color: var(--st-text-inverse);
  font-weight: 700;
  padding: 0.75rem;
  border-radius: var(--st-radius-sm);
  font-size: 15px;
}
.nps-enviar:hover:not(:disabled) {
  background: #219150;
  border-color: #219150;
}
.nps-ok {
  font-size: 3.5rem;
  color: var(--st-success);
}
.nps-footer {
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.75rem;
  margin-top: 20px;
  margin-bottom: 0;
}
@media (max-width: 480px) {
  .nps-page { padding: 24px 12px 48px; }
  .nps-card { padding: 22px 16px; }
  .nps-notas { grid-template-columns: repeat(6, minmax(0, 1fr)); }
  .nps-ident { grid-template-columns: 1fr; }
}
</style>
